import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Alert,
  Box, 
  Button, 
  Checkbox,
  Container, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel, 
  LinearProgress,
  Paper, 
  Snackbar,
  TextField, 
  Typography
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, ArrowBack as ArrowBackIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useAuth } from '../store/authStore';
import { uploadFile, listFiles } from '../api/storage';

const StorageUploadPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, checkAuth } = useAuth();
  
  // Form state
  const [file, setFile] = useState(null);
  const [path, setPath] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [authChecked, setAuthChecked] = useState(false);
  
  // Overwrite confirmation state
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [existingFile, setExistingFile] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  
  // Check authentication on mount
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await checkAuth();
        setAuthChecked(true);
      } catch (err) {
        console.error('Authentication check failed:', err);
        setError('Authentication failed. Please log in again.');
        navigate('/login', { state: { from: '/storage/upload' } });
      }
    };
    
    verifyAuth();
  }, [checkAuth, navigate]);
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (authChecked && !isAuthenticated) {
      navigate('/login', { state: { from: '/storage/upload' } });
    }
  }, [isAuthenticated, authChecked, navigate]);
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };
  
  // Check if file exists with same name and path
  const checkForExistingFile = async (fileName, filePath) => {
    try {
      console.log('Checking for existing file:', fileName, 'in path:', filePath);
      const response = await listFiles(1, { 
        search: fileName,
        path: filePath,
        limit: 100 
      });
      
      console.log('List files response:', response);
      
      if (response && response.files) {
        // Look for exact match of filename and path
        const normalizedPath = filePath.trim() || '';
        console.log('Looking for exact match. Normalized path:', normalizedPath);
        
        const existingFile = response.files.find(f => {
          const filePathNormalized = (f.path || '').trim();
          console.log('Comparing file:', f.name, 'path:', filePathNormalized, 'with target:', fileName, normalizedPath);
          return f.name === fileName && filePathNormalized === normalizedPath;
        });
        
        console.log('Found existing file:', existingFile);
        return existingFile;
      }
      return null;
    } catch (err) {
      console.error('Error checking for existing file:', err);
      return null;
    }
  };

  const performUpload = async (uploadData) => {
    try {
      const result = await uploadFile(1, uploadData.file, { 
        path: uploadData.path,
        isPublic: uploadData.isPublic 
      });
      
      if (result && result.success) {
        setSuccess('File uploaded successfully!');
        setFile(null);
        setPath('');
        setIsPublic(false);
        
        // Reset file input
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';
        
        // Redirect to storage page after a short delay
        setTimeout(() => {
          navigate('/storage');
        }, 1500);
      } else {
        throw new Error(result?.message || 'Failed to upload file');
      }
      
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'An error occurred while uploading the file');
      
      // If it's an auth error, redirect to login
      if (err.response?.status === 401 || 
          err.message.includes('expired') || 
          err.message.includes('authentication') || 
          err.message.includes('log in')) {
        navigate('/login', { state: { from: '/storage/upload' } });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated || !authChecked) {
      setError('Please log in to upload files');
      return;
    }
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    setIsUploading(true);
    setError('');
    setSuccess('');
    
    // Check for existing file
    const existingFile = await checkForExistingFile(file.name, path);
    
    console.log('Existing file check result:', existingFile);
    
    if (existingFile) {
      // File exists, show overwrite confirmation
      console.log('File exists, showing overwrite dialog');
      setExistingFile(existingFile);
      setPendingUpload({ file, path, isPublic });
      setShowOverwriteDialog(true);
      setIsUploading(false);
    } else {
      // No existing file, proceed with upload
      console.log('No existing file found, proceeding with upload');
      await performUpload({ file, path, isPublic });
    }
  };

  const handleOverwriteConfirm = async () => {
    setShowOverwriteDialog(false);
    setIsUploading(true);
    
    if (pendingUpload) {
      await performUpload(pendingUpload);
    }
    
    // Clean up
    setExistingFile(null);
    setPendingUpload(null);
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteDialog(false);
    setExistingFile(null);
    setPendingUpload(null);
    setIsUploading(false);
  };
  
  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Storage
      </Button>
      
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Upload File to Storage
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{ py: 2, mb: 1 }}
            >
              {file ? file.name : 'Select File'}
              <input
                id="file-upload"
                type="file"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="caption" color="textSecondary">
              {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'No file selected'}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Path (optional)"
            placeholder="e.g., documents/reports"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            margin="normal"
            helperText="Optional subdirectory path within the storage"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                color="primary"
              />
            }
            label="Make this file publicly accessible"
            sx={{ mt: 2, mb: 3 }}
          />
          
          {isUploading && <LinearProgress sx={{ mt: 2, mb: 2 }} />}
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!file || isUploading}
            fullWidth
            size="large"
          >
            {isUploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </Box>
      </Paper>
      
      <Snackbar 
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
      
      {/* Overwrite confirmation dialog */}
      <Dialog
        open={showOverwriteDialog}
        onClose={handleOverwriteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          File Already Exists
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            A file named <strong>"{existingFile?.name}"</strong> already exists 
            {existingFile?.path && ` in the path "${existingFile.path}"`}.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Do you want to overwrite the existing file? This action cannot be undone.
          </DialogContentText>
          {existingFile && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Existing file:</strong>
              </Typography>
              <Typography variant="body2">
                Size: {(existingFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Typography variant="body2">
                Uploaded: {new Date(existingFile.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Visibility: {existingFile.is_public ? 'Public' : 'Private'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverwriteCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleOverwriteConfirm} 
            color="warning" 
            variant="contained"
            startIcon={<WarningIcon />}
          >
            Overwrite File
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StorageUploadPage;
